import React, { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { shades } from '../../theme';

export const ThemeContext = createContext();

// Define custom colors mapping
export const customColors = {
	// Light theme
	light: {
		drawerBackground: '#FFFFFF',
		drawerText: '#2C3E50',
		brandColor: '#1570EF',
		borderColor: '#F0F1F3',
		buttonHover: 'rgba(0, 0, 0, 0.04)',
		searchBackground: '#ffffff',
		searchBorder: '#F0F1F3',
		cardBackground: '#FFFFFF',
		cardText: '#2C3E50',
		cardSubtitle: '#6B7280',
		cardBorder: 'rgba(0, 0, 0, 0.1)',
		statLabelColor: '#64748B',
		successColor: '#10B981',
		warningColor: '#F59E0B',
		dangerColor: '#EF4444',
		paperBackground: '#FFFFFF',
		tableHeader: '#F8FAFC',
		activeMenuBorder: '#1570EF',
		menuText: '#2C3E50',
		headerBackground: 'white',
	},
	dark: {
		// Dark theme alternatives
		drawerBackground: '#1A1A2E',
		drawerText: '#E0E0E0',
		brandColor: '#4A99FF',
		borderColor: '#2D3748',
		buttonHover: 'rgba(255, 255, 255, 0.1)',
		searchBackground: '#2D3748',
		searchBorder: '#4A5568',
		cardBackground: '#1A1A2E',
		cardText: '#E0E0E0',
		cardSubtitle: '#A0AEC0',
		cardBorder: 'rgba(255, 255, 255, 0.1)',
		statLabelColor: '#A0AEC0',
		successColor: '#4ADE80',
		warningColor: '#FFA94D',
		dangerColor: '#FF6B6B',
		paperBackground: '#1E1E2E',
		tableHeader: '#2D3748',
		activeMenuBorder: '#4A99FF',
		menuText: '#E0E0E0',
		headerBackground: '#1A1A2E',
	},
};

const ThemeProviderComponent = ({ children }) => {
	// Restore initial mode detection
	const getInitialMode = () => {
		const storedMode = localStorage.getItem('themeMode');
		if (['light', 'dark', 'system'].includes(storedMode)) return storedMode;
		return 'system';
	};

	// Use the detected initial mode
	const [mode, setMode] = useState(getInitialMode);
	const [systemPreference, setSystemPreference] = useState(
		window.matchMedia('(prefers-color-scheme: dark)').matches
			? 'dark'
			: 'light',
	);

	// Listen for system theme preference changes
	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

		const handleSystemChange = (e) => {
			setSystemPreference(e.matches ? 'dark' : 'light');
		};

		// Initial check
		handleSystemChange(mediaQuery);

		// Add listener
		if (mediaQuery.addEventListener) {
			mediaQuery.addEventListener('change', handleSystemChange);
			return () => mediaQuery.removeEventListener('change', handleSystemChange);
		} else {
			// Fallback for older browsers
			mediaQuery.addListener(handleSystemChange);
			return () => mediaQuery.removeListener(handleSystemChange);
		}
	}, []);

	// Determine effective mode (what actually gets applied)
	const effectiveMode = useMemo(() => {
		return mode === 'system' ? systemPreference : mode;
	}, [mode, systemPreference]);

	// Save selected mode to localStorage
	useEffect(() => {
		localStorage.setItem('themeMode', mode);
	}, [mode]);

	// Theme toggle handler
	const toggleTheme = (newMode) => {
		if (['light', 'dark', 'system'].includes(newMode)) {
			setMode(newMode);
		} else {
			// Toggle between light and dark
			setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
		}
	};

	// Get custom colors based on current theme
	const colors = useMemo(() => {
		const colorSet = effectiveMode === 'dark' ? 'dark' : 'light';
		return customColors[colorSet];
	}, [effectiveMode]);

	// Create MUI theme based on effective mode
	const muiTheme = useMemo(() => {
		return createTheme({
			palette: {
				mode: effectiveMode,
				primary: {
					main:
						effectiveMode === 'dark' ? colors.brandColor : shades.primary[500],
					light: shades.primary[300],
					dark: shades.primary[700],
					contrastText: '#fff',
				},
				secondary: {
					main: shades.secondary[500],
					light: shades.secondary[300],
					dark: shades.secondary[700],
					contrastText: '#fff',
				},
				background: {
					default: effectiveMode === 'dark' ? '#121212' : shades.white[100],
					paper:
						effectiveMode === 'dark'
							? colors.paperBackground
							: shades.white[500],
				},
				text: {
					primary: effectiveMode === 'dark' ? '#f8f9fa' : shades.secondary[500],
					secondary: effectiveMode === 'dark' ? '#adb5bd' : shades.neutral[700],
					disabled:
						effectiveMode === 'dark'
							? 'rgba(255, 255, 255, 0.5)'
							: 'rgba(0, 0, 0, 0.38)',
				},
				action: {
					active: effectiveMode === 'dark' ? '#fff' : shades.secondary[500],
					hover:
						effectiveMode === 'dark'
							? colors.buttonHover
							: 'rgba(0, 0, 0, 0.04)',
					selected:
						effectiveMode === 'dark'
							? 'rgba(255, 255, 255, 0.16)'
							: 'rgba(0, 0, 0, 0.08)',
					disabled:
						effectiveMode === 'dark'
							? 'rgba(255, 255, 255, 0.3)'
							: 'rgba(0, 0, 0, 0.26)',
					disabledBackground:
						effectiveMode === 'dark'
							? 'rgba(255, 255, 255, 0.12)'
							: 'rgba(0, 0, 0, 0.12)',
				},
				divider:
					effectiveMode === 'dark'
						? 'rgba(255, 255, 255, 0.12)'
						: 'rgba(0, 0, 0, 0.12)',
				// Add custom colors to the theme
				customColors: colors,
			},
			typography: {
				fontFamily: ['Didact Gothic', 'sans-serif'].join(','),
				fontSize: 14,
				fontWeight: 400,
				h1: {
					fontFamily: ['Cardo', 'serif'].join(','),
					fontSize: 48,
					fontWeight: 400,
				},
				h2: {
					fontFamily: ['Cardo', 'serif'].join(','),
					fontSize: 36,
					fontWeight: 400,
				},
				h3: {
					fontFamily: ['Cardo', 'serif'].join(','),
					fontSize: 20,
					fontWeight: 400,
				},
				h4: {
					fontFamily: ['Cardo', 'serif'].join(','),
					fontSize: 16,
					fontWeight: 400,
				},
			},
			components: {
				// Add component style overrides
				MuiDrawer: {
					styleOverrides: {
						paper: ({ theme }) => ({
							backgroundColor: theme.palette.customColors.drawerBackground,
							color: theme.palette.customColors.drawerText,
							transition: theme.transitions.create(
								['background-color', 'color'],
								{
									duration: theme.transitions.duration.standard,
								},
							),
						}),
					},
				},
				MuiAppBar: {
					styleOverrides: {
						root: ({ theme }) => ({
							backgroundColor: theme.palette.customColors.headerBackground,
							color: theme.palette.customColors.drawerText,
							transition: theme.transitions.create(
								['background-color', 'color', 'border-color'],
								{
									duration: theme.transitions.duration.standard,
								},
							),
						}),
					},
				},
			},
		});
	}, [effectiveMode, colors]);

	return (
		<ThemeContext.Provider value={{ mode, effectiveMode, toggleTheme, colors }}>
			<ThemeProvider theme={muiTheme}>{children}</ThemeProvider>
		</ThemeContext.Provider>
	);
};

ThemeProviderComponent.propTypes = {
	children: PropTypes.node.isRequired,
};

export default ThemeProviderComponent;
